import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Form,
  Button,
  Toast,
  InputGroup,
  Modal,
  ProgressBar,
} from "react-bootstrap";
import axios from "axios";
import { BACKEND_URL, USA_STATES } from "constants";
import {
  Eye,
  EyeSlash,
  CheckCircleFill,
  ExclamationTriangleFill,
  XCircleFill,
} from "react-bootstrap-icons";
import TermsAndConditions from "pages/layout/footer/Agb";
import { userRegistered } from "../../features/user/userSlice";

const MobileRegistrationForm = () => {
  const dispatch = useDispatch();

  const [formValues, setFormValues] = useState({
    username: "",
    email: "",
    password: "",
    repeatPassword: "",
    age: "",
    gender: "",
    state: "",
    agb: false,
  });

  const [filteredStates, setFilteredStates] = useState(USA_STATES);
  const [loading, setLoading] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);

  const feedbackLabels = useMemo(
    () => ({
      200: {
        message:
          "Thank you for your registration. Please check your email to verify your account!",
        color: "success",
        icon: <CheckCircleFill className="me-2" />,
      },
      400: {
        message: "Username or Email already exists and is already verified!",
        color: "danger",
        icon: <XCircleFill className="me-2" />,
      },
      409: {
        message:
          "Account exists but not verified. Check your email for verification.",
        color: "warning",
        icon: <ExclamationTriangleFill className="me-2" />,
      },
      500: {
        message: "Something went wrong! Please try again later!",
        color: "danger",
        icon: <XCircleFill className="me-2" />,
      },
    }),
    []
  );

  const [registeredFeedbacks, setRegisteredFeedbacks] = useState([]);

  const usernameRegex = /^[a-zA-Z0-9_]{3,}$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // Updated password regex: at least 8 chars, at least 1 letter, at least 1 number
  const passwordRegex = /^(?=.*[a-zA-Z])(?=.*\d).{8,}$/;

  const isUsernameValid = usernameRegex.test(formValues.username);
  const isEmailValid = emailRegex.test(formValues.email);
  const isPasswordValid = passwordRegex.test(formValues.password);
  const isRepeatPasswordValid =
    formValues.password && formValues.password === formValues.repeatPassword;
  const isAgeValid = parseInt(formValues.age, 10) >= 18;
  const isGenderValid = !!formValues.gender;
  const isStateValid = !!formValues.state;
  const isAgbValid = formValues.agb;

  const isFormValid =
    isUsernameValid &&
    isEmailValid &&
    isPasswordValid &&
    isRepeatPasswordValid &&
    isAgeValid &&
    isGenderValid &&
    isStateValid &&
    isAgbValid;

  const calculatePasswordStrength = (password) => {
    let strength = 0;

    if (password.length >= 8) strength += 25;
    if (/[a-zA-Z]/.test(password)) strength += 25; // Updated to check for any letter
    if (/\d/.test(password)) strength += 25;
    if (/[@$!%*?&]/.test(password)) strength += 25;

    return strength;
  };

  const passwordStrength = calculatePasswordStrength(formValues.password);

  const sanitizeInput = (value) => value.replace(/[<>]/g, "");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" ? checked : sanitizeInput(value);

    setFormValues((prev) => ({ ...prev, [name]: val }));

    if (name === "state") {
      setFilteredStates(
        USA_STATES.filter((s) =>
          s.toLowerCase().startsWith(val.toLowerCase())
        )
      );
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isFormValid) return;

    setLoading(true);

    const payload = {
      username: formValues.username,
      password: formValues.password,
      age: formValues.age,
      email: formValues.email,
      gender: formValues.gender,
      state: formValues.state,
    };

    const MIN_DELAY = 3000;
    const startTime = Date.now();

    axios
      .post(`${BACKEND_URL}/register`, payload)
      .then((response) => {
        dispatch(userRegistered(response.data));

        setRegisteredFeedbacks((prev) => [
          ...prev,
          { id: Date.now(), ...feedbackLabels[200] },
        ]);
      })
      .catch((error) => {
        console.error(error);

        dispatch(userRegistered(error.response?.data || {}));

        const statusCode = error.response?.status;
        const feedback = feedbackLabels[statusCode] || feedbackLabels[500];

        setRegisteredFeedbacks((prev) => [
          ...prev,
          { id: Date.now(), ...feedback },
        ]);
      })
      .finally(() => {
        const elapsed = Date.now() - startTime;
        const delay = Math.max(MIN_DELAY - elapsed, 0);

        setTimeout(() => {
          setLoading(false);
        }, delay);
      });
  };

  const renderInput = (props, isValid, isInvalid) => (
    <InputGroup>
      <Form.Control {...props} isValid={isValid} isInvalid={isInvalid} />
    </InputGroup>
  );

  const helperStyle = {
    minHeight: "0.75rem",
    display: "block",
    marginBottom: "0.15rem",
  };

  return (
    <div className="mobile-registration-wrapper">
      <Form onSubmit={handleSubmit}>
        <Form.Group>
          {renderInput(
            {
              type: "text",
              name: "username",
              placeholder: "Username",
              value: formValues.username,
              onChange: handleChange,
              required: true,
            },
            isUsernameValid,
            formValues.username && !isUsernameValid
          )}

          <Form.Text className="text-muted" style={helperStyle}>
            {formValues.username && !isUsernameValid
              ? "3+ letters, numbers, or underscore"
              : ""}
          </Form.Text>
        </Form.Group>

        <Form.Group>
          {renderInput(
            {
              type: "email",
              name: "email",
              placeholder: "Email",
              value: formValues.email,
              onChange: handleChange,
              required: true,
            },
            isEmailValid,
            formValues.email && !isEmailValid
          )}

          <Form.Text className="text-muted" style={helperStyle}>
            {formValues.email && !isEmailValid ? "Invalid email format" : ""}
          </Form.Text>
        </Form.Group>

        <Form.Group>
          <ProgressBar
            now={passwordStrength}
            label={`${passwordStrength}%`}
            className="mt-2"
            variant={
              passwordStrength < 50
                ? "danger"
                : passwordStrength < 75
                ? "warning"
                : "success"
            }
          />

          <InputGroup className="mt-1">
            <Form.Control
              type={showPasswords ? "text" : "password"}
              name="password"
              placeholder="Password"
              value={formValues.password}
              onChange={handleChange}
              required
              isValid={!!formValues.password && isPasswordValid}
              isInvalid={!!formValues.password && !isPasswordValid}
            />

            <Button
              variant="outline-secondary"
              onClick={() => setShowPasswords(!showPasswords)}
              type="button"
            >
              {showPasswords ? <EyeSlash /> : <Eye />}
            </Button>
          </InputGroup>

          <Form.Text className="text-muted" style={helperStyle}>
            {/* Updated helper text to match new rules */}
            {formValues.password && !isPasswordValid
              ? "8+ chars, at least 1 letter and 1 number"
              : ""}
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-1">
          <Form.Control
            type={showPasswords ? "text" : "password"}
            name="repeatPassword"
            placeholder="Confirm Password"
            value={formValues.repeatPassword}
            onChange={handleChange}
            required
            isValid={!!formValues.repeatPassword && isRepeatPasswordValid}
            isInvalid={!!formValues.repeatPassword && !isRepeatPasswordValid}
          />

          <Form.Text className="text-muted" style={helperStyle}>
            {formValues.repeatPassword && !isRepeatPasswordValid
              ? "Passwords do not match"
              : ""}
          </Form.Text>
        </Form.Group>

        <Form.Group>
          {renderInput(
            {
              type: "number",
              name: "age",
              placeholder: "Age",
              value: formValues.age,
              onChange: handleChange,
              min: 18,
              required: true,
            },
            isAgeValid,
            formValues.age && !isAgeValid
          )}

          <Form.Text className="text-muted" style={helperStyle}>
            {formValues.age && !isAgeValid ? "Age 18+" : ""}
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3" style={{ position: "relative" }}>
          <InputGroup>
            <Form.Control
              type="text"
              name="state"
              value={formValues.state}
              onChange={handleChange}
              placeholder="State"
              required
              autoComplete="off"
            />

            <Button
              variant="outline-secondary"
              onClick={() => setShowStateDropdown(!showStateDropdown)}
              type="button"
            >
              ▼
            </Button>
          </InputGroup>

          {showStateDropdown && (
            <div className="mobile-state-dropdown">
              {filteredStates.map((stateName) => (
                <div
                  key={stateName}
                  className="mobile-state-option"
                  onClick={() => {
                    handleChange({
                      target: { name: "state", value: stateName },
                    });
                    setShowStateDropdown(false);
                  }}
                >
                  {stateName}
                </div>
              ))}
            </div>
          )}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Check
            inline
            type="radio"
            value="male"
            name="gender"
            label="Male"
            onChange={handleChange}
            checked={formValues.gender === "male"}
            required
          />

          <Form.Check
            inline
            type="radio"
            value="female"
            name="gender"
            label="Female"
            onChange={handleChange}
            checked={formValues.gender === "female"}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            name="agb"
            label={
              <>
                I accept the{" "}
                <span
                  style={{
                    color: "#007bff",
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowTerms(true);
                  }}
                >
                  terms and conditions
                </span>
              </>
            }
            checked={formValues.agb}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Button
          type="submit"
          disabled={!isFormValid || loading}
          variant="primary"
          className="w-100 mb-1"
        >
          {loading ? "Registering..." : "Register"}
        </Button>
      </Form>

      <Modal
        show={showTerms}
        onHide={() => setShowTerms(false)}
        size="lg"
        centered
        scrollable
      >
        <Modal.Body>
          <TermsAndConditions />
        </Modal.Body>
      </Modal>

      <div className="mt-2">
        {registeredFeedbacks.map((fb) => (
          <Toast
            key={fb.id}
            bg={fb.color}
            onClose={() =>
              setRegisteredFeedbacks((prev) =>
                prev.filter((toast) => toast.id !== fb.id)
              )
            }
            delay={3000}
            autohide
            animation
            className="mb-1"
          >
            <Toast.Body className="text-white d-flex align-items-center">
              {fb.icon}
              {fb.message}
            </Toast.Body>
          </Toast>
        ))}
      </div>
    </div>
  );
};

export default MobileRegistrationForm;